import { useAccessControl } from '@/hooks';
import { PrescriptionAssistantModal } from '@/shared-ui/PrescriptionAssistantModal/PrescriptionAssistantModal';
import { UserRole } from '@/types';
import { Logger } from '@/utils/logger';
import { SearchTextField } from '@montugroup/design-system';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, List, ListItem, styled, Typography, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useCancelProduct } from './hooks/useCancelProduct';
import { usePrescriptionSearch } from './hooks/usePrescriptionSearch';
import { PrescriptionList } from './PrescriptionList';
import type { CancelProductPredicate, PrescriptionMedicationsTableRow } from './PrescriptionMedicationsTable.types';
import { isProductCancellable as isProductCancellableUtil } from './utils/isProductCancellable';

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  width: '100%',
  marginTop: theme.spacing(1),

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),

  [theme.breakpoints.up('md')]: {
    width: 'auto',
    flexWrap: 'nowrap'
  }
}));

interface PrescriptionSearchProps {
  isEditable: boolean;
  handleCreateScript: () => void;
  patientId: number;
}

export const PrescriptionSearch = ({ isEditable, handleCreateScript, patientId }: PrescriptionSearchProps) => {
  const { allowAccess, controlAccess } = useAccessControl();
  const theme = useTheme();
  const logger = new Logger('PrescriptionSearch');

  const { searchTerm, data, isLoading, isSuccess, isError, handleRefresh, handleInputChange, handleClearSearchInput } =
    usePrescriptionSearch({
      patientId
    });

  const { handleCancelProduct, cancelInProgress, modalState, closeModal, confirmCancelProduct } = useCancelProduct({
    patientId,
    logger
  });

  const hasCancelPermissions = controlAccess([UserRole.Doctor, UserRole.SuperAdmin, UserRole.Manager], true);

  const isProductCancellable: CancelProductPredicate = useCallback(
    (product: PrescriptionMedicationsTableRow) =>
      isProductCancellableUtil({
        product,
        isPrescriptionsEditable: isEditable,
        hasUserPermissions: hasCancelPermissions,
        cancelInProgress
      }),
    [cancelInProgress, isEditable]
  );

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      <HeaderContainer>
        <SearchTextField
          handleChange={handleInputChange}
          handleClear={handleClearSearchInput}
          labelText="Search"
          enableClear={true}
          placeholder="Prescription ID or medication"
          debounceTime={0}
        />

        <ButtonsContainer>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<RefreshIcon />}
            aria-label="refresh"
            onClick={handleRefresh}
          >
            Refresh
          </Button>

          {allowAccess(
            [UserRole.Doctor],
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!isEditable}
              aria-label="create new script"
              onClick={handleCreateScript}
            >
              Create new script
            </Button>
          )}
        </ButtonsContainer>
      </HeaderContainer>

      <PrescriptionList
        data={data?.data ?? []}
        searchTerm={searchTerm}
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        onCancelProduct={handleCancelProduct}
        isProductCancellable={isProductCancellable}
        hasCancelPermissions={hasCancelPermissions}
      />

      <PrescriptionAssistantModal
        open={modalState.isOpen}
        title="Cancel Prescription"
        severity="warning"
        body={
          <>
            <Typography variant="body1" color={theme.palette.text.primary} sx={{ marginBottom: theme.spacing(3) }}>
              Are you sure you want to cancel this prescription?
            </Typography>
            <List>
              <ListItem sx={{ padding: '0px', fontWeight: 700, color: theme.palette.text.primary }}>
                {modalState.productName}
              </ListItem>
            </List>
          </>
        }
        confirmLabel="Cancel Prescription"
        closeLabel="Not now"
        onConfirm={confirmCancelProduct}
        onClose={closeModal}
      />
    </Box>
  );
};
